import { lazyRouteComponent } from "@tanstack/react-router";
import { Gauge } from "lucide-react";

import type { PageDef } from "@/app/registry";

export const PERIODS = ["1D", "1W", "1M", "1Y"] as const;
export const CHART_KINDS = ["line", "candles"] as const;
export const MOVER_KINDS = ["gainers", "losers"] as const;
export const EXTREMES = ["high52", "low52"] as const;
export const UNIVERSES = ["nifty50", "nifty100", "nifty500"] as const;
export const CAPS = ["all", "large", "mid", "small"] as const;
export const CONTRIB = ["NIFTY", "BANKNIFTY"] as const;

export interface MarketSearch {
  /** Selected index in the overview (Angel One's short symbol). */
  idx: string;
  period: (typeof PERIODS)[number];
  chart: (typeof CHART_KINDS)[number];
  /** Top movers: gainers or losers. */
  mv: (typeof MOVER_KINDS)[number];
  universe: (typeof UNIVERSES)[number];
  cap: (typeof CAPS)[number];
  /** Sector filter for the top movers (NSE industry). */
  sector?: string;
  /** Selected sector tab in "Sectorwise movements". */
  sec?: string;
  hl: (typeof EXTREMES)[number];
  contrib: (typeof CONTRIB)[number];
}

function pick<T extends readonly string[]>(list: T, v: unknown, fallback: T[number]): T[number] {
  return list.includes(v as string) ? (v as T[number]) : fallback;
}

function text(v: unknown, max: number): string | undefined {
  return typeof v === "string" && v.trim() && v.length <= max ? v : undefined;
}

export function parseMarketSearch(s: Record<string, unknown>): MarketSearch {
  const idx = text(s.idx, 40);
  const sector = text(s.sector, 80);
  const sec = text(s.sec, 80);
  return {
    idx: idx && /^[A-Z0-9 &-]+$/.test(idx) ? idx : "NIFTY",
    period: pick(PERIODS, s.period, "1D"),
    chart: pick(CHART_KINDS, s.chart, "line"),
    mv: pick(MOVER_KINDS, s.mv, "gainers"),
    universe: pick(UNIVERSES, s.universe, "nifty500"),
    cap: pick(CAPS, s.cap, "all"),
    ...(sector ? { sector } : {}),
    ...(sec ? { sec } : {}),
    hl: pick(EXTREMES, s.hl, "high52"),
    contrib: pick(CONTRIB, s.contrib, "NIFTY"),
  };
}

export const pages: PageDef[] = [
  {
    path: "/app/market",
    title: "Market",
    component: lazyRouteComponent(() => import("./MarketPage"), "MarketPage"),
    nav: { label: "Market", icon: Gauge, group: "Markets", order: 0 },
    validateSearch: parseMarketSearch,
  },
];
